<template>
    <div :class="{'card shadow my-2 bg-body rounded h-auto py-4 px-3 py-md-4 px-md-5' : !isModal}">
        <div v-if="!hasReachedMaxFailedAttempts && !!canConfirmPhone">
            <h5 class="text-center text-capitalize" :class="{'mt-3 mb-0' : !isModal}">
                Verify your identity by confirming your phone number
            </h5>
            <div :class="{'mb-3' : !isModal}">
                <div class="input-group mt-3">
                    <input type="text" class="form-control form-control-lg" :class="{'is-invalid' : !!errorMessage}"
                           @input="handleInput"
                           v-model="phoneNumber"
                           :placeholder="phoneNumberProp"
                           maxlength="10"
                    >
                    <div v-if="!!errorMessage" class="invalid-feedback mt-2">
                        {{ errorMessage }}
                    </div>
                    <button class="btn btn-outline-success"
                            style="min-width: 100px;"
                            v-if="!isModal"
                            @click="doConfirmPhoneRequest"
                            type="button">Verify</button>
                </div>
                <small v-if="failedAttempts>0" class="position-absolute bottom-1 end-0 me-4 text-muted">Remaining attempts: {{ maxFailedAttempts - failedAttempts}}</small>
            </div>
        </div>
        <div v-else>
            <h4 class="text-center my-0" :class="{'mt-3 mb-0' : !isModal}">
                You have reached the maximum number of attempts. Please try again later.
            </h4>
        </div>
    </div>
</template>
<script setup>
import { ref, computed } from 'vue';
import { useLoadingState } from '@/composables/useLoadingState.js';
import { useRefreshResultsStorage } from '@/composables/useRefreshResultsStorage.js';
import { postAndReturnData } from '@/utils/api.js';
import { getBaseUrl } from '@/utils/url.js';
import { extractDigits } from '@/utils/string-formatting.js';

const props = defineProps({
    phoneNumberProp: {
        type: String,
        required: false,
    },
    uid: {
        type: String,
        required: true,
    },
    isModal: {
        type: Boolean,
        required: false,
        default: false,
    },
    canConfirmPhone: {
        type: Boolean,
        required: false,
        default: false,
    },
    failedAttempts: {
        type: Number,
        required: true,
        default: 0,
    },
    maxFailedAttempts: {
        type: Number,
        required: false,
        default: 3,
    },
});

const emit = defineEmits(['phone-confirmed', 'phone-confirmation-failed']);

const { isLoading, errorMessage } = useLoadingState();
const { get: getStoredResults, set: setStoredResults } = useRefreshResultsStorage(props.uid);
const phoneNumber = ref(null);
const phoneNumberConfirmed = ref(false);
const locationUrl = getBaseUrl();

const isPhoneNumberValid = computed(() => {
    return phoneNumber.value && phoneNumber.value.length === 10;
});

const hasReachedMaxFailedAttempts = computed(() => {
    return props.failedAttempts >= props.maxFailedAttempts;
});

function clearPhoneNumber() {
    phoneNumber.value = null;
}

async function doConfirmPhoneRequest() {
    if (!isPhoneNumberValid.value) {
        errorMessage.value = 'Number must be 10 digits.';
        return;
    }

    const requestResponse = await submitRequest('confirmation', {
        uid: props.uid,
        phone: phoneNumber.value,
        source: locationUrl,
    });

    if (requestResponse) {
        switch (requestResponse.status) {
            case 'success':
                phoneNumberConfirmed.value = true;
                setStoredResults({
                    phoneNumberConfirmed: phoneNumberConfirmed.value,
                    phoneNumber: phoneNumber.value,
                });
                emit('phone-confirmed', phoneNumber.value);
                break;

            case 'disabled':
                errorMessage.value = 'You have reached the maximum number of attempts. Please try again later.';
                clearPhoneNumber();
                emit('phone-confirmation-failed', 3);
                break;

            case 'error':
            default:
                errorMessage.value = 'Incorrect phone number. Please try again.';
                clearPhoneNumber();
                emit('phone-confirmation-failed');
                break;
        }
    } else {
        errorMessage.value = 'Incorrect phone number. Please try again.';
        clearPhoneNumber();
        if (hasReachedMaxFailedAttempts.value) {
            emit('phone-confirmation-failed');
        }
        return;
    }
}

async function submitRequest(type = 'confirmation', data) {
    isLoading.value = true;

    const requestEndpoints = {
        'confirmation': '/portal/phone/confirmation',
    };

    return await postAndReturnData(requestEndpoints[type], data);
}

function handleInput(event) {
    phoneNumber.value = extractDigits(event.target.value);
}

defineExpose({ doConfirmPhoneRequest });
</script>
